import * as _ from 'lodash';
import nvSharedServicesModule from './nv.shared.services.module';

angular.module(nvSharedServicesModule).factory('enqueuedPatchFieldsMixin', enqueuedPatchFieldsMixin);

function enqueuedPatchFieldsMixin(UtilitiesService, Rollbar) {
  const _generateMixMethods = receivingClass => {
    return {
      _updateUpdatedAt() {
        this._updatedAt = new Date();
      },

      getUpdatedAt() {
        return this._updatedAt;
      },

      trackServerState() {
        this._serverState = this.toPOJO();
      },

      resetToServerState() {
        if (!this._serverState) {
          throw new Error("server state isn't being tracked");
        }
        this.applyPatch(this._serverState);
      },

      getServerState() {
        return this._serverState;
      },

      hasQueuedPatches() {
        return (this._queuedPatches || []).length > 0;
      },

      hasChanges() {
        const oldState = this._serverState;
        const currState = this.toPOJO();
        const isEqual = angular.equals(oldState, currState);
        return !isEqual;
      },

      _removePatchFromQueue(patch) {
        this._queuedPatches = _.without(this._queuedPatches, patch);
      },

      $enqueuedPatchFields(fields, successCallback, errorCallback) {
        let self = this;
        const fieldsArray = _.isArray(fields) ? fields : [fields];
        // Initialize _queuedPatches array
        self._queuedPatches = _.isUndefined(self._queuedPatches) ? [] : self._queuedPatches;

        // Generate unique ID for each request
        let id = Math.floor((1 + Math.random()) * 0x10000).toString(16);

        let patch = self.generatePatch(fields);

        // changed line 68, from Student to baseClass to handle school as well
        let promise = receivingClass.patch.call(
          this,
          { id: self._id },
          patch,
          function() {
            // Find the resolved request and remove from the que
            let successfulPatch: any = _.find(self._queuedPatches, { id });

            if (successfulPatch) {
              self._removePatchFromQueue(successfulPatch);
              delete successfulPatch.patch.$resolved;

              // Copy the successfully saved property on the _serverState
              if (self._serverState) {
                UtilitiesService.patchObject(self._serverState, successfulPatch.patch);
              }

              self._updateUpdatedAt();
            } else {
              // Not sure why this would not be found
              // Logging error so that there is a stack trace (JC)
              Rollbar.debug(new Error('Patch could not be removed from queue'), self._queuedPatches);
            }

            if (successCallback) {
              return successCallback();
            }
          },
          function(error) {
            // Find the resolved request and remove from the que
            let failedPatch = _.find(self._queuedPatches, { id });
            self._removePatchFromQueue(failedPatch);

            if (self._enqueuedPatchFieldsErrorHandlers) {
              _.each(self._enqueuedPatchFieldsErrorHandlers, function(handler: any) {
                handler.call(self, error, failedPatch);
              });
            }

            if (error && errorCallback) {
              return errorCallback(error);
            }
          },
        );

        self._queuedPatches.push({
          id,
          fields: fieldsArray,
          patch,
        });

        if (self._enqueuedPatchFieldsEnqueuedHandlers) {
          _.each(self._enqueuedPatchFieldsEnqueuedHandlers, (handler: any) => {
            handler.call(self, patch, fieldsArray);
          });
        }

        return promise.$promise || promise;
      },
      /**
       * Register a callback that is called whenever there is an error with an patch from $enqueuedPatchFields
       *
       * Callbacks are called with the signature: errorResponse, failedPatch; and with `this` set to the Student
       *
       * @param {Function} callback
       * @return {Function} a function that, when called, will de-register the callback
       */
      $onEnqueuedPatchFieldsError(callback) {
        let self = this;
        self._enqueuedPatchFieldsErrorHandlers = self._enqueuedPatchFieldsErrorHandlers || [];
        self._enqueuedPatchFieldsErrorHandlers.push(callback);
        return function() {
          _.remove(self._enqueuedPatchFieldsErrorHandlers, function(handler) {
            return handler === callback;
          });
        };
      },
      /**
       * Register a callback that is called whenever a new patch using $enqueuedPatchFields is enqueued
       *
       * Callbacks are called with two arguments: `patch` and `fields`. Patch is the full student patch and
       * fields are the fields that were used to extract the patch from the student. In the callback,
       * `this` set to the Student instance.
       *
       * @param {Function} callback
       * @return {Function} a function that, when called, will de-register the callback
       */
      $onEnqueuedPatchFieldsEnqueued(callback) {
        const self = this;
        self._enqueuedPatchFieldsEnqueuedHandlers = self._enqueuedPatchFieldsErrorHandlers || [];
        self._enqueuedPatchFieldsEnqueuedHandlers.push(callback);
        return function() {
          _.remove(self._enqueuedPatchFieldsEnqueuedHandlers, handler => {
            return handler === callback;
          });
        };
      },
    };
  };

  return receivingClass => {
    const mixin = _generateMixMethods(receivingClass);
    if (!_.has(receivingClass.prototype, 'toPOJO') || !_.has(receivingClass, 'patch')) {
      console.error('Receiving class must contain required methods: toPOJO, patch');
      throw new Error('Receiving class must contain required methods: toPOJO, patch');
    }

    for (let method in mixin) {
      if (!Object.hasOwnProperty(method)) {
        receivingClass.prototype[method] = mixin[method];
      }
    }
    return receivingClass;
  };
}
